// @flow
import React from "react";
import {Table} from "antd";
import {useTranslation} from "react-i18next";

import {orderTablePropType} from "./courierStatisticsTablePropTypes";
import {toMinute} from "../../../../utils/helpers";
import {useStore} from "../../../../hooks/useStore";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const CourierStatisticsTable = ({
    loading,
    data,
    dataCount,
    setPage,
    currentPage
}: orderTablePropType) => {
    const stores = useStore();
    const {t} = useTranslation(["courierStatisticsTable"]);
    const defaultSorter = (a, b, key) => a[key] - b[key];
    const courierSorter = (a, b) => a?.courierName.localeCompare(b?.courierName);
    const subCompanySorter = (a, b) => {
        if (!a.subcompanies || !b.subcompanies) return true;
        return a?.subcompanies[0].localeCompare(b?.subcompanies[0]);
    };
    return (
        <>
            <Table
                loading={loading}
                dataSource={data}
                rowKey="courierName"
                data-testid="orderTable"
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                scroll={{x: "max-content"}}
            >
                <Column
                    title={t("fields.courier", {ns: "common"})}
                    dataIndex="courierName"
                    render={courierName => <div data-testid="courier">{courierName || "-"}</div>}
                    sorter={courierSorter}
                />
                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                    <Column
                        title={t("fields.company", {ns: "common"})}
                        dataIndex="company"
                        render={company => <div data-testid="company">{company}</div>}
                    />
                )}
                <Column
                    title={t("fields.subcompany", {ns: "common"})}
                    dataIndex="subcompanies"
                    render={subCompanies => (
                        <div data-testid="subCompany">
                            {subCompanies && subCompanies.length > 0 && subCompanies[0]
                                ? subCompanies[0]
                                : "-"}
                        </div>
                    )}
                    sorter={subCompanySorter}
                />
                <Column
                    title={t("tableFields.totalOrder", {ns: "courierStatisticsTable"})}
                    dataIndex="totalDelivery"
                    render={(totalDelivery: Number) => (
                        <div data-testid="totalDelivery">{totalDelivery || "-"}</div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "totalDelivery")}
                />
                <Column
                    title={t("tableFields.orderPerDelivery", {ns: "courierStatisticsTable"})}
                    dataIndex="orderPerDelivery"
                    render={(orderPerDelivery: Number) => (
                        <div data-testid="orderPerDelivery">
                            {orderPerDelivery.toFixed(2).replace(".00", "") || "-"}
                        </div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "orderPerDelivery")}
                />
                <Column
                    title={t("tableFields.avgDelivery", {ns: "courierStatisticsTable"})}
                    dataIndex="averageDeliveryTime"
                    render={(averageDeliveryTime: Number) => (
                        <div data-testid="averageDeliveryTime">
                            {toMinute(averageDeliveryTime) || "-"}
                        </div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "averageDeliveryTime")}
                />
                <Column
                    title={t("tableFields.lateDelivery", {ns: "courierStatisticsTable"})}
                    dataIndex="lateDelivered"
                    render={(lateDelivered: Number) => (
                        <div data-testid="lateDelivered">{lateDelivered || "-"}</div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "lateDelivered")}
                />
                <Column
                    title={t("tableFields.avgDepartureTime", {ns: "courierStatisticsTable"})}
                    dataIndex="averageDepartureTime"
                    render={(averageDepartureTime: Number) => (
                        <div data-testid="averageDepartureTime">
                            {toMinute(averageDepartureTime) || "-"}
                        </div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "averageDepartureTime")}
                />
                <Column
                    title={t("tableFields.avgArriveToCustomer", {ns: "courierStatisticsTable"})}
                    dataIndex="averagePickUpToDeliveryAdressTime"
                    render={(averagePickUpToDeliveryAdressTime: Number) => (
                        <div data-testid="averagePickUpToDeliveryAdressTime">
                            {toMinute(averagePickUpToDeliveryAdressTime) || "-"}
                        </div>
                    )}
                    sorter={(a, b) => defaultSorter(a, b, "averagePickUpToDeliveryAdressTime")}
                />
                <Column
                    title={t("tableFields.avgDeliveryTime", {ns: "courierStatisticsTable"})}
                    dataIndex="averageDeliveryAdressTimeToHandOverTime"
                    render={(averageDeliveryAdressTimeToHandOverTime: Number) => (
                        <div data-testid="averageDeliveryAdressTimeToHandOverTime">
                            {toMinute(averageDeliveryAdressTimeToHandOverTime) || "-"}
                        </div>
                    )}
                    sorter={(a, b) =>
                        defaultSorter(a, b, "averageDeliveryAdressTimeToHandOverTime")
                    }
                />
            </Table>
        </>
    );
};
